import React from "react";
import { useNavigate } from "react-router-dom";

import useActivation, { ACTIVATION_REQUEST_STATE } from "@/hooks/useActivation";
import { usePermissions } from "@/hooks/usePermissions";

// MA-UI-078: Global Activation Status Indicator
//
// P4-A1: this rendered for EVERY authenticated user and issued GET /activation
// on every AppShell mount. That endpoint is RequireModule("settings"), which
// WP-8A governance constrains to Account Owner/Admin, so Manager, Sales Agent,
// Booking Agent and Customer Service Agent each got a 403 -- which the hook
// then rendered as the red "System Not Ready" badge. Four roles saw a false
// alarm about a system they hold no authority over, and every one of them sent
// a request that could only ever be refused.
//
// The gate is `settings` in effectiveModules, deliberately: it is the SAME
// authorization the API enforces, so the projection cannot drift from it. A
// role-name predicate would be a second copy of a policy that already exists,
// and would not survive an Account whose governance differs.
//
// `isResolvedForSelectedAccount` (P4-WP-9A.2a) is what keeps this correct
// during hydration and across an Account switch: it is true only when the
// permissions in hand belong to the CURRENTLY selected Account, so Account A's
// settings authority can never authorize a request for Account B while B is
// still resolving.
export default function ActivationStatusIndicator() {
  const navigate = useNavigate();
  const { effectiveModules, isResolvedForSelectedAccount } = usePermissions();

  const authorized =
    isResolvedForSelectedAccount && effectiveModules.includes("settings");

  const { isActivated, status, requestState } = useActivation({
    enabled: authorized,
  });

  if (!authorized) return null;
  // Only an authorized, successful response may render anything. An
  // unauthorized or failed request is NOT an activation problem.
  if (requestState !== ACTIVATION_REQUEST_STATE.READY) return null;
  if (isActivated) return null; // Don't show indicator when activated

  const config = {
    incomplete: {
      label: "System Not Ready",
      className: "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15",
      dot: "bg-amber-400",
    },
    not_ready: {
      label: "System Not Ready",
      className: "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/15",
      dot: "bg-red-400",
    },
  };
  const c = config[status] || config.not_ready;

  return (
    <button
      onClick={() => navigate("/activation")}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-medium transition-colors cursor-pointer ${c.className}`}
      data-testid="global-activation-indicator"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </button>
  );
}
